/**
 * ReportPreview — a window that has not been filed, opened to see what is in it. Same
 * contents renderer as the filed detail, because it is the same
 * projection with the emit skipped.
 *
 * While editing a filed report (`refileTarget` set) it also offers the items
 * eligible to add, an Undo row per removed item, and re-export instead of filing.
 */

import type { Dispatch, StateUpdater } from "preact/hooks";
import { BackLink } from "./BackLink";
import { ReportContents } from "./ReportContents";
import type { FileableWindow, FiledReport } from "../reports/report-client";
import { KIND_LABEL, clientBlocked, leavesTheCompany, occurrenceId } from "../reports/report-labels";
import type { ReportIncludedItem, ReportKind, ReportSidecar } from "../reports/report-types";
import { CLIENT_REPORT_COMING_SOON } from "../triage/client-report-gate";

export interface ReportPreviewProps {
  previewWindow: FileableWindow;
  refileTarget: FiledReport | null;
  sidecar: ReportSidecar | null;
  sidecarError: string | null;
  curationBusy: string | null;
  curationError: string | null;
  removedItems: Map<string, ReportIncludedItem>;
  selectedAdditions: Set<string>;
  setSelectedAdditions: Dispatch<StateUpdater<Set<string>>>;
  confirmWindow: FileableWindow | null;
  setConfirmWindow: (w: FileableWindow | null) => void;
  closePreview: () => void;
  setItemIncluded: (item: ReportIncludedItem, included: boolean) => Promise<void>;
  addSelectedItems: () => Promise<void>;
  approvePreview: (w: FileableWindow) => Promise<void>;
  fetchPhoto?: (media_ref: string) => Promise<Blob | null>;
  onOpenItem?: (capture_id: string) => void;
  onSetItemIncluded?: (capture_id: string, report_kind: ReportKind, included: boolean) => Promise<boolean>;
  onComingSoon?: (message: string) => void;
}

export function ReportPreview({
  previewWindow,
  refileTarget,
  sidecar,
  sidecarError,
  curationBusy,
  curationError,
  removedItems,
  selectedAdditions,
  setSelectedAdditions,
  confirmWindow,
  setConfirmWindow,
  closePreview,
  setItemIncluded,
  addSelectedItems,
  approvePreview,
  fetchPhoto,
  onOpenItem,
  onSetItemIncluded,
  onComingSoon,
}: ReportPreviewProps) {
  return (
    <div data-testid="report-preview-detail">
      <BackLink label="Reports" testid="report-preview-back" onBack={closePreview} />
      <p class="report-detail-title" data-testid="report-preview-title">
        {KIND_LABEL[previewWindow.report_kind]} report
      </p>
      <p class="report-detail-meta" data-testid="report-preview-meta">
        {previewWindow.label} · {refileTarget === null ? "not filed yet" : "editing filed report"}
      </p>

      <ReportContents
        prefix="report-preview"
        heading="What would go in this report"
        whereFallback="Mark can't show what's in this one yet."
        sidecar={sidecar}
        sidecarError={sidecarError}
        onOpenItem={onOpenItem}
        fetchPhoto={fetchPhoto}
        canCurate={refileTarget !== null && onSetItemIncluded !== undefined}
        curationBusy={curationBusy}
        onRemove={(item) => void setItemIncluded(item, false)}
      />
      {refileTarget !== null && (sidecar?.eligible_additions?.length ?? 0) > 0 && (
        <section class="report-detail-included" data-testid="report-preview-eligible">
          <p class="save-chip-group-hint">Eligible items not in this report</p>
          <p class="report-detail-meta">Select the project items to add. Nothing is added automatically.</p>
          <ul>
            {sidecar!.eligible_additions!.map((item) => (
              <li key={occurrenceId(item)} data-testid={`report-preview-eligible-${item.capture_id}`}>
                <label>
                  <input
                    type="checkbox"
                    data-testid={`report-preview-select-${item.capture_id}`}
                    checked={selectedAdditions.has(occurrenceId(item))}
                    disabled={curationBusy !== null}
                    onChange={() => {
                      setConfirmWindow(null);
                      setSelectedAdditions((current) => {
                      const next = new Set(current);
                      if (next.has(occurrenceId(item))) next.delete(occurrenceId(item));
                      else next.add(occurrenceId(item));
                      return next;
                      });
                    }}
                  />
                  <span class="report-item-title">{item.title}</span>
                  {item.meta && <span class="report-item-meta">{item.meta}</span>}
                </label>
              </li>
            ))}
          </ul>
          <button
            type="button"
            class="report-file-action"
            data-testid="report-preview-add-selected"
            disabled={curationBusy !== null || selectedAdditions.size === 0}
            onClick={() => void addSelectedItems()}
          >
            {curationBusy === "__additions__" ? "Adding and reconciling…" : `Add selected item${selectedAdditions.size === 1 ? "" : "s"}`}
          </button>
        </section>
      )}
      {refileTarget !== null && [...removedItems.values()].map((item) => (
        <div key={occurrenceId(item)} class="report-curation-undo" data-testid={`report-preview-removed-${item.capture_id}`}>
          <span>Removed “{item.title}”</span>
          <button
            type="button"
            data-testid={`report-preview-undo-${item.capture_id}`}
            disabled={curationBusy !== null}
            onClick={() => void setItemIncluded(item, true)}
          >
            {curationBusy === occurrenceId(item) ? "Restoring…" : "Undo"}
          </button>
        </div>
      ))}
      {curationError !== null && <p data-testid="report-preview-curation-error">{curationError}</p>}

      {/* Filing from here takes the SAME gates as the list row — §4.7 / rule
          #7: anything that leaves the company is never one tap, and being on
          a detail screen is not an exception. */}
      {confirmWindow && (
        <div class="report-send-confirm" data-testid="report-preview-confirm-panel">
          <p class="report-send-confirm-q">
            {refileTarget !== null
              ? "File a new revision of this weekly? It goes to the subs and designers on this job; the existing version stays available."
              : confirmWindow.report_kind === "client"
                ? "File this as the CLIENT report?"
                : "This weekly goes to the subs and designers on this job, and names who owns each open item. File it?"}
          </p>
          <button
            type="button"
            data-testid="report-preview-file-confirm"
            disabled={curationBusy !== null || curationError !== null || sidecar === null || sidecarError !== null || selectedAdditions.size > 0}
            onClick={() => void approvePreview(confirmWindow)}
          >
            {refileTarget === null ? "Yes, file it" : "Yes, re-export it"}
          </button>
          <button
            type="button"
            data-testid="report-preview-file-cancel"
            onClick={() => setConfirmWindow(null)}
          >
            Cancel
          </button>
        </div>
      )}
      <button
        type="button"
        class="report-file-action"
        data-testid="report-preview-file"
        disabled={curationBusy !== null || curationError !== null || sidecar === null || sidecarError !== null || selectedAdditions.size > 0}
        aria-disabled={clientBlocked(previewWindow.report_kind) || curationBusy !== null || curationError !== null || sidecar === null || sidecarError !== null || selectedAdditions.size > 0}
        onClick={() => {
          if (curationBusy !== null || curationError !== null || sidecar === null || sidecarError !== null || selectedAdditions.size > 0) return;
          if (clientBlocked(previewWindow.report_kind)) {
            onComingSoon?.(CLIENT_REPORT_COMING_SOON);
            return;
          }
          if (leavesTheCompany(previewWindow.report_kind)) setConfirmWindow(previewWindow);
          else void approvePreview(previewWindow);
        }}
      >
        {refileTarget === null ? "Approve and file" : "Approve and re-export"}
      </button>
    </div>
  );
}
